/**
 * Formatação no padrão brasileiro: R$ 1.234,56 · 22,3% · 09/10/2026.
 *
 * Lê: nada.
 * Usado por: telas/, graficos/ e importacao/ (para montar mensagens).
 */
import type { DataIso, Fonte, Mes } from './dominio/tipos.ts';

/** Nome de cada painel na tela: o Winthor é a Aliança (Amend, Cora...); o SAP é a Bauny. */
export const NOME_DO_PAINEL: Record<Fonte, string> = { winthor: 'Aliança', 'sap-bauny': 'Bauny' };
/** Nome do sistema de onde vem o faturamento. */
export const NOME_DO_SISTEMA: Record<Fonte, string> = { winthor: 'Winthor', 'sap-bauny': 'SAP' };

const MOEDA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const MOEDA_INTEIRA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const NOMES_DOS_MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

/** R$ 1.234,56 */
export function formatarMoeda(valor: number): string {
  return MOEDA.format(valor);
}

/** R$ 1.235 (sem centavos, para números grandes de destaque) */
export function formatarMoedaInteira(valor: number): string {
  return MOEDA_INTEIRA.format(valor);
}

/** 0,2226 → "22,3%". null vira "—". */
export function formatarPorcentagem(fracao: number | null, casas = 1): string {
  if (fracao === null || !Number.isFinite(fracao)) return '—';
  return `${(fracao * 100).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`;
}

export function formatarNumero(valor: number, casas = 0): string {
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

/** "2026-10-09" → "09/10/2026" */
export function formatarData(data: DataIso): string {
  const [ano, mes, dia] = data.split('-');
  return `${dia}/${mes}/${ano}`;
}

/** "2026-10-09" → "09/10" */
export function formatarDataCurta(data: DataIso): string {
  const [, mes, dia] = data.split('-');
  return `${dia}/${mes}`;
}

/** "2026-10-12 14:32:05" (como vem da API) → "12/10/2026 14:32" */
export function formatarDataHora(texto: string): string {
  const [data = '', hora = ''] = texto.split(/[ T]/);
  return `${formatarData(data)} ${hora.slice(0, 5)}`.trim();
}

/** "2026-10" → "outubro de 2026" */
export function nomeDoMes(mes: Mes): string {
  const [ano, numero] = mes.split('-').map(Number) as [number, number];
  return `${NOMES_DOS_MESES[numero - 1] ?? '?'} de ${ano}`;
}

/** "2026-09" → "set/2026" */
export function mesAbreviado(mes: Mes): string {
  const nome = NOMES_DOS_MESES[Number(mes.slice(5, 7)) - 1] ?? '?';
  return `${nome.slice(0, 3)}/${mes.slice(0, 4)}`;
}

/** "2026-10" → "Outubro" */
export function nomeCurtoDoMes(mes: Mes): string {
  const nome = NOMES_DOS_MESES[Number(mes.slice(5, 7)) - 1] ?? '?';
  return nome.charAt(0).toUpperCase() + nome.slice(1);
}

/** "JOÃO PAULO" → "João Paulo" (para nomes vindos em maiúsculas). */
export function nomeProprio(texto: string): string {
  const minusculas = new Set(['da', 'de', 'do', 'das', 'dos', 'e']);
  return texto
    .toLowerCase()
    .split(' ')
    .map((palavra, i) => (i > 0 && minusculas.has(palavra) ? palavra : palavra.charAt(0).toUpperCase() + palavra.slice(1)))
    .join(' ');
}

/** "Guilherme Cavalieri" → "GC", "Ewertom" → "E", "Evangelino (sócio)" → "ES" (para o avatar sem foto). */
export function iniciais(nome: string): string {
  const partes = nome.replace(/[^\p{L}\p{N}\s]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  const primeira = partes[0]?.charAt(0) ?? '';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.charAt(0) ?? '') : '';
  return (primeira + ultima).toUpperCase();
}

/** ["Amend", "Cora", "The Pro"] → "Amend, Cora e The Pro" */
export function listar(itens: string[]): string {
  if (itens.length <= 1) return itens[0] ?? 'nenhuma';
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}
